import Parse from "parse"
import { getTimezoneDate, parseLimitRequest } from "../../utils"
import dayjs from "dayjs"
import {
    createOrderSupplierItem, getOrderSupplierItemsBySupplierItem
} from "./parseOrderSupplierItemManager"
import {
    addLotEvent,
    createLot, deleteLot, getLotsByOrderSupplierItem
} from "../lot/parseLotManager"
import moment from "moment"
import cloneDeep from "lodash/cloneDeep"
import { creditNotesType } from "../../utils/creditNoteUtils"
import { createCreditNote, deleteCreditNote, getCreditNotesForSupplierItem } from "./parseCreditNotesManager"
import { getLot, getSameLotExists, isLotOfSupplierItem } from "../lot/parseLotManager"
import { getOrderTotalAmount } from "../../utils/orderReception"
import { getLotEventUser } from "../../utils/lotEventUser"

const Orders = Parse.Object.extend("Orders")

export async function getOrders(site, date, toJson = true) {
    try {
        const orders = (await new Parse.Query(Orders)
            .include(["supplierItems.supplierItem", "supplierItems.lots", "site.stockZones", "creditNotes", "supplierItems.creditNotes", "supplierItems.lots.creditNotes"])
            .notEqualTo("deleted", true)
            .equalTo("site", site)
            .equalTo("receptionDate", moment.utc(parseFloat(date)).startOf("day").valueOf())
            .limit(parseLimitRequest)
            .find()) || []

        return toJson ? orders.map(order => order.toJSON()) : orders
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function getOrdersForStatus(site, status = "TO_VERIFY", toJson = true) {
    try {
        const orders = (await new Parse.Query(Orders)
            .include(["supplierItems.supplierItem", "supplierItems.lots", "site.stockZones"])
            .equalTo("site", site)
            .equalTo("orderStatus", status)
            .notEqualTo("deleted", true)
            .ascending("orderNumber")
            .find()) || []

        return toJson ? orders.map(el => el.toJSON()) : orders
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function getOrdersReceptions(site, values, toJson = true) {
    try {

        const query = await new Parse.Query(Orders)
            .include(["supplierItems.supplierItem", "site"])
            .equalTo("site", site)
            .containedIn("orderStatus", ["DONE", "CANCELLED"])
            .notEqualTo("deleted", true)

        if (values.suppliers && values.suppliers.length > 0) {
            query.containedIn("supplier.objectId", values.suppliers.map((supplier) => supplier.value))
        }

        if (values.receptionStatus && values.receptionStatus.length > 0) {
            query.containedIn("receptionStatus", values.receptionStatus)
        }

        if (values.deliveryNoteNumber) {
            query.matches("deliveryNote.number", values.deliveryNoteNumber.toString(), "i")
        }

        if (values.receptionPeriodStart) {
            query.greaterThanOrEqualTo("receptionDate", getTimezoneDate(values.receptionPeriodStart, "timestamp", true))
        }

        if (values.receptionPeriodEnd) {
            query.lessThanOrEqualTo("receptionDate", getTimezoneDate(values.receptionPeriodEnd, "timestamp", false))
        }

        if (values.orderDate) {
            const orderDateEndOfDay = dayjs(values.orderDate).endOf("day").format()
            query
                .greaterThanOrEqualTo("orderDate", getTimezoneDate(values.orderDate, "timestamp", true))
                .lessThanOrEqualTo("orderDate", getTimezoneDate(orderDateEndOfDay, "timestamp", false))
        }

        let { sortBy = "asc", sortField = null } = values
        if (sortField !== null) {
            sortBy === "asc" ? query.ascending(sortField) : query.descending(sortField)
        }

        let orders = await query.limit(parseLimitRequest).find() || []
        if (values.orderNumber) {
            orders = orders.filter(ord => ord.get("orderNumber").toString().includes(values.orderNumber))
        }

        if (values.supplierItem) {
            orders = orders.filter(ord => {
                return ord.get("supplierItems").find(orderSupplierItem => {
                    return orderSupplierItem.supplierItem.get("supplierItemId") === values.supplierItem.value
                })
            })
        }

        return toJson ? orders.map((order) => order.toJSON()) : orders
    }
    catch (e) {
        return Promise.reject(e)
    }
}
/**
 * 
 * @param {Array} sites // array parse objects 
 * @param {Boolean} toJson 
 * @param {Object} filters 
 * @param {String} type  // creditNote | billings
 * @returns 
 */
export const getOrdersBySites = async (sites, toJson = true, filters = {}, type) => {
    try {
        if (!filters.dataPage) {
            filters.dataPage = 0
        }

        const sitesForQuery = filters.site ? [sites.find(site => site.id === filters.site.objectId)] : sites

        const query = new Parse.Query(Orders)
            .notEqualTo("deleted", true)
            .containedIn("site", sitesForQuery)

        if (type && type === "creditNote") {
            query.exists("creditNotesAmount")
                .greaterThan("creditNotesAmount", 0)
        }

        let { sortBy = "asc", sortField = null } = filters

        if (!sortField && type === "creditNote") {
            sortBy = "desc"
            sortField = "creditNotesAmount"
        }
        if (!sortField && type === "billings") {
            sortBy = "asc"
            sortField = "receptionDate"
        }

        sortBy === "asc" ? query.ascending(sortField) : query.descending(sortField)

        if (filters.deliveryNoteNumber) {
            query.matches("deliveryNote.number", filters.deliveryNoteNumber, "i")
        }

        if (filters.supplier) {
            query.containedIn("supplier.objectId", [filters.supplier.objectId])
        }

        if (filters.suppliers && filters.suppliers.length > 0) { // to remove still used for tab "avoirs" (removed next ticket)
            query.containedIn("supplier.objectId", filters.suppliers.map((supplier) => supplier.value))
        }

        if (filters.isCreditNotesPaid) {
            query.containedIn("isCreditNotesPaid", filters.isCreditNotesPaid)
        }

        if (filters.receptionPeriodStart) {
            query.greaterThanOrEqualTo("receptionDate", getTimezoneDate(filters.receptionPeriodStart, "timestamp", true))
        } else {
            // default reception start date filter
            const periodStart = type === "creditNote" ? dayjs().subtract(3, "months").startOf("day").valueOf() : dayjs().startOf("month").startOf("day").valueOf()
            query.greaterThan("receptionDate", periodStart)

        }

        if (filters.receptionPeriodEnd) {
            query.lessThanOrEqualTo("receptionDate", getTimezoneDate(filters.receptionPeriodEnd, "timestamp", false))
        } else {
            // default reception end date filter
            const periodEnd = type === "creditNote" ? dayjs().startOf("day").valueOf() : dayjs().startOf("day").valueOf()
            query.lessThanOrEqualTo("receptionDate", periodEnd)
        }

        if (filters.isBillingErrorOptions) {
            query.containedIn("billError", filters.isBillingErrorOptions)
        } else {
            // default billError filter
            query.containedIn("billError", [true, false])
        }

        if (filters.include) {
            query.include(filters.include)
        }

        let orders, total
        if (!filters.orderNumber) {
            [orders, total] = await Promise.all([
                (await query
                    .limit(parseLimitRequest)
                    .find()) || [],
                await query.count()
            ])
        } else {
            orders = await query.limit(parseLimitRequest).find()
            orders = orders.filter(ord => ord.get("orderNumber").toString().includes(filters.orderNumber))
            total = orders.length
        }

        let result = orders
        if (toJson) {
            result = orders.map((order) => order.toJSON())
        }

        return {
            orders: result,
            total
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function getOrder(orderId, toJson = true) {
    try {
        const order = (await new Parse.Query(Orders)
            .equalTo("objectId", orderId)
            .include([
                "site.stockZones", "supplierItems.supplierItem", "supplierItems.lots",
                "supplierItems.lots.orderSupplierItem",
                "creditNotes", "supplierItems.creditNotes", "supplierItems.lots.creditNotes",
                "user"
            ])
            .first()) || null

        return order ? toJson ? order.toJSON() : order : null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function getOrderByOrderNumber(orderNumber, toJson = true) {
    try {
        const order = (await new Parse.Query(Orders)
            .equalTo("orderNumber", orderNumber)
            .include(["site", "supplierItems.supplierItem", "supplierItems.lots"])
            .first()) || null

        return order ? toJson ? order.toJSON() : order : null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function createOrUpdateOrder(values, orderId = null) {
    try {
        let order

        if (!orderId) {
            order = new Orders()
        }
        else {
            order = await getOrder(orderId, false)
        }

        Object.entries(values).forEach(([key, value]) => {
            order.set(key, value)
        })

        await order.save()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function createOrderReception(site, date, supplier) {
    try {
        const order = new Orders()

        const lastOrderNumber = (await new Parse.Query(Orders)
            .select("orderNumber")
            .descending("createdAt")
            .first()) || null

        const orderNumber = lastOrderNumber ? lastOrderNumber.get("orderNumber") + 1 : 1

        order.set("site", site)
        order.set("supplier", supplier)
        order.set("receptionDate", date)
        order.set("orderNumber", orderNumber)
        order.set("orderStatus", "DONE")
        order.set("receptionStatus", "TODO")
        order.set("supplierItems", [])

        await order.save()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export const addOrderSupplierItem = async (orderId, supplierItemId) => {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            const orderSupplierItem = await createOrderSupplierItem(supplierItemId)
            const supplierItems = [...order.get("supplierItems")]

            const newSupplierItem = {
                supplierItem: orderSupplierItem,
                quantities: { order: 0 },
                receptionStatus: "TODO",
                lots: [],
                supplierItemId,
                deliveryNoteUnitPrice: null
            }
            supplierItems.push(newSupplierItem)

            order.set("supplierItems", supplierItems)

            await order.save()
            return await getOrder(orderId, true)
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function createOrderSupplierItemsForOrder(values, receptionStatus = null) {
    try {
        const supplierItems = []

        for (const { card, quantity } of values) {
            const orderSupplierItem = await createOrderSupplierItem(card.objectId)

            const newSupplierItem = {
                supplierItem: orderSupplierItem,
                quantities: { order: quantity },
                receptionStatus: receptionStatus,
                supplierItemId: card.objectId,
                lots: [],
                deliveryNoteUnitPrice: null
            }

            supplierItems.push(newSupplierItem)
        }

        return supplierItems
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function deleteOrder(orderId) {
    try {
        const order = await getOrder(orderId, false)

        await order.destroy()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function cancelOrder(orderId, toJSON = true) {
    try {
        const order = await getOrder(orderId, false)

        order.set("orderStatus", "CANCELLED")
        order.set("receptionStatus", "CANCELLED")

        const newOrder = await order.save()

        return toJSON ? newOrder.toJSON() : newOrder
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function updateOrderSupplierItem(orderId, supplierItemId, status, values) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            const supplierItems = [...order.get("supplierItems")]
            const current = supplierItems.find(el => el.supplierItem.id === supplierItemId)

            if (current) {
                if (!current.quantities) current.quantities = {}

                if (values) {
                    current.quantities.reception = values.quantity
                    current.cancelQuantitiesCreditNotes = values.cancelQuantitiesCreditNotes
                }
                current.receptionStatus = status

                order.set("supplierItems", supplierItems)

                const updatedOrder = await order.save()

                return updatedOrder.toJSON()
            }

            return null
        }

        return null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function updateOrderShippingFees(orderId, shippingFees) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            const supplier = order.get("supplier")

            if (!supplier.order) supplier.order = {}
            supplier.order.shippingFees = Number(shippingFees)
            order.set("supplier", supplier)

            const updatedOrder = await order.save()

            return updatedOrder.toJSON()
        }

        return null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export const updateArticleDeliveryNoteQuantityAndPrice = async (orderId, supplierItemId, values) => {
    try {
        const order = await getOrder(orderId, false)

        if (!order) return

        const supplierItems = order.get("supplierItems")?.map(orderSupplierItem => {
            if (orderSupplierItem.supplierItem.id === supplierItemId) {
                orderSupplierItem.quantities.deliveryNote = Number(values.quantity)
                if (values.deliveryNoteUnitPrice) {
                    orderSupplierItem.deliveryNoteUnitPrice = Number(values.deliveryNoteUnitPrice)
                }
            }
            return orderSupplierItem
        })

        order.set("supplierItems", supplierItems || [])

        const updatedOrder = await order.save()

        return updatedOrder.toJSON()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

/**
 * 
 * @param {*} orderId  
 * @param {*} values {supplierItemId: quantity}
 * @returns {Promise<Object|null>}
 */
export const updateArticleDeliveryNoteQuantities = async (orderId, values) => {
    try {
        const order = await getOrder(orderId, false)
        if (!order) return
        const supplierItemIds = Object.keys(values)
        const supplierItems = order.get("supplierItems")?.map(orderSupplierItem => {
            if (supplierItemIds.includes(orderSupplierItem.supplierItem.id)) {
                orderSupplierItem.quantities.deliveryNote = Number(values[orderSupplierItem.supplierItem.id])
            }
            return orderSupplierItem
        })
        order.set("supplierItems", supplierItems || [])

        const updatedOrder = await order.save()

        return updatedOrder.toJSON()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function updateArticleBillQuantityAndPrice(orderId, supplierItemId, values) {

    const order = await getOrder(orderId, false)
    if (order) {
        const supplierItems = [...order.get("supplierItems")]
        const supplierItem = supplierItems.find(el => el.supplierItem.id === supplierItemId)

        if (supplierItem) {
            supplierItem.quantities.bill = Number(values.quantity)
            supplierItem.billUnitPrice = Number(values.billUnitPrice)

            order.set("totalAmount", supplierItems.reduce((amount, item) => {
                const accumulatedValue = item.quantities.bill * item.billUnitPrice + amount
                return accumulatedValue
            }, 0))
            order.set("supplierItems", supplierItems)

            const updatedOrder = await order.save()
            return updatedOrder.toJSON()
        }
    }
    return null
}

export const initializeDeliveryNoteUnitPrices = async (orderId, isRefusedReception = false) => {
    try {
        const order = await getOrder(orderId, false)
        if (!order) return

        // no need to the deliveryNoteUnitPrice if the order is refused
        if (!isRefusedReception) {
            const supplierItems = [...order.get("supplierItems")]

            supplierItems.map(supplierItem => {
                if (!supplierItem.deliveryNoteUnitPrice) {
                    supplierItem.deliveryNoteUnitPrice = supplierItem.supplierItem.get("units").stock.price
                }

                return supplierItem
            })

            order.set("supplierItems", supplierItems)

            const updatedOrder = await order.save()

            return updatedOrder.toJSON()
        }

        return order.toJSON()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function startOrderReception(orderId) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            order.set("receptionStatus", "IN_PROGRESS")
            order.set("receptionDate", moment.utc().startOf("day").valueOf())

            const newOrder = await order.save()

            return newOrder.toJSON()
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export const getCreditNotesLotsTotalAmount = (lots = []) => {
    let lotsTotalAmount = 0
    for (const lot of lots) {
        if (lot.creditNotes) {
            const totalAmount = lot.creditNotes.reduce((acc, creditNote) => {
                return acc + creditNote.amount
            }, 0)

            lotsTotalAmount += totalAmount
        }
    }

    return lotsTotalAmount
}

export const updateOrderCreditNotesByTotalAmount = async (orderId) => {
    try {
        const order = await getOrder(orderId, false)
        const orderJson = order.toJSON()

        let totalCreditNoteAmount = 0

        for (const supplierItem of orderJson.supplierItems) {
            // credit notes for supplier item global
            if (supplierItem.creditNotes) {
                const ids = supplierItem.creditNotes.map(creditNote => creditNote.objectId)
                let price

                if (supplierItem.deliveryNoteUnitPrice) {
                    price = supplierItem.deliveryNoteUnitPrice
                }
                else {
                    price = supplierItem.supplierItem.units.order.price
                }

                const creditNotes = await getCreditNotesForSupplierItem(ids, false)

                for (const creditNote of creditNotes) {
                    if (creditNote.get("type") === creditNotesType.BROKEN) {
                        const quantity = creditNote.get("quantity")
                        creditNote.set("amount", parseFloat(price * quantity))
                    }

                    if (creditNote.get("amount") !== null) {
                        totalCreditNoteAmount += creditNote.get("amount")
                    }

                    await creditNote.save()
                }
            }

            // credit notes for supplier item lots
            if (supplierItem.lots) {
                totalCreditNoteAmount += getCreditNotesLotsTotalAmount(supplierItem.lots)
            }
        }

        order.set("creditNotesAmount", totalCreditNoteAmount)
        await order.save()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

/**
 * the creditNotesAmount is the same as the order totalAmount
 * instead of the sum of creditNotes amount
 * when the order is refused
 */
export const updateOrderCreditNotesAmount = async (orderId, totalAmount) => {
    try {
        const order = await getOrder(orderId, false)

        if (!order) return

        order.set("creditNotesAmount", totalAmount)
        await order.save()
    }
    catch (e) {
        return Promise.reject(e)
    }
}


export async function closeOrderReception(orderId, creditNotes, totalAmount) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            const supplierItems = [...order.get("supplierItems").map((supplierItem) => ({
                ...supplierItem,
                billUnitPrice: supplierItem.deliveryNoteUnitPrice,
                quantities: {
                    ...supplierItem.quantities,
                    bill: supplierItem.quantities.deliveryNote != null ? supplierItem.quantities.deliveryNote : supplierItem.quantities.reception
                }
            }))]

            if (creditNotes && creditNotes.size > 0) {
                await Promise.all(supplierItems.map(async supplierItem => {
                    const creditNoteData = creditNotes.get(supplierItem.supplierItem.id)

                    const newCreditNotes = []

                    if (creditNoteData && Array.isArray(creditNoteData)) {
                        await Promise.all(creditNoteData.map(async el => {
                            const creditNote = await createCreditNote(el, creditNotesType.DELIVERY_NOTE, supplierItem.supplierItem)

                            newCreditNotes.push(creditNote)
                        }))
                    }

                    supplierItem.creditNotes = Array.isArray(supplierItem.creditNotes)
                        ? [...supplierItem.creditNotes, ...newCreditNotes]
                        : newCreditNotes
                }))
            }

            order.set("supplierItems", supplierItems)
            order.set("receptionStatus", "DONE")
            order.set("totalAmount", totalAmount)
            order.set("billError", false)
            order.set("isCreditNotesPaid", false)

            const updatedOrder = await order.save()
            return updatedOrder.toJSON()
        }

        return null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function saveOrderDeliveryNote(orderId, values) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            if (!order.get("deliveryNote")?.inputDate) {
                values.inputDate = moment.utc().valueOf()
            } else {
                values.inputDate = order.get("deliveryNote").inputDate
            }

            order.set("deliveryNote", values)

            const newOrder = await order.save()

            return newOrder.toJSON()
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

const applyLotCreditNotes = async (parseLot, lotId, creditNotes) => {
    const lotCreditNotesData = creditNotes.get(lotId) || []
    if (lotCreditNotesData.length === 0) return false

    let decrementedQuantity = 0
    const lotCreditNotes = await Promise.all(lotCreditNotesData.map(async creditNoteEntry => {
        const newCreditNote = await createCreditNote(creditNoteEntry.creditNote, creditNotesType.LOT, parseLot)
        await addLotEvent(parseLot.id, {
            mode: "BROKEN",
            user: getLotEventUser(),
            date: moment.utc().valueOf(),
            quantity: newCreditNote.get("quantity"),
            reason: newCreditNote.get("reason"),
            comment: newCreditNote.get("comment"),
            images: newCreditNote.get("images")
        })
        decrementedQuantity += newCreditNote.get("quantity")
        return newCreditNote
    }))

    parseLot.set("creditNotes", [...(parseLot.get("creditNotes") || []), ...lotCreditNotes])
    if (decrementedQuantity > 0) {
        parseLot.set("quantity", parseLot.get("quantity") - decrementedQuantity)
    }
    return true
}

export const createOrderLots = async (orderId, orderSupplierItem, lots, creditNotes) => {
    try {
        const order = await getOrder(orderId, false)
        const lotsToUpdate = []
        const lotsToCreate = []
        const createdLots = [] // track newly created lots to link them back to the order
        const alreadySavedLots = [] // track lots already in DB that just need to be linked to the order

        for (const lot of lots) {
            if (lot.objectId) {
                // Lot was already saved in a previous (possibly failed) attempt — don't recreate or
                // increment it; just collect a pointer so it gets properly linked to the order below.
                const Lot = Parse.Object.extend("Lots")
                const ref = new Lot()
                ref.id = lot.objectId
                alreadySavedLots.push(ref)
            } else if (lot.existingLot) {
                lotsToUpdate.push(lot)
            } else {
                lotsToCreate.push(lot)
            }
        }

        if (order) {
            const copyOrderSupplierItems = cloneDeep(order.get("supplierItems"))
            const targetOsiId = orderSupplierItem.supplierItem.id || orderSupplierItem.supplierItem.objectId
            const currentOrderSupplierItem = copyOrderSupplierItems.find(el => {
                const elId = el.supplierItem.id || el.supplierItem.objectId
                return elId === targetOsiId
            })
            // Pointer to the current OrderSupplierItem — stored in RECEPTION events for filtering
            const currentOsiId = currentOrderSupplierItem?.supplierItem?.id || currentOrderSupplierItem?.supplierItem?.objectId
            const currentOsiPointer = { __type: "Pointer", className: "OrderSupplierItem", objectId: currentOsiId }

            // Supplier item and units are read from the order just fetched: the client state may have lost them,
            // and without them a lot of another supplier item with the same number, DLC and zone would be completed
            const fetchedOsi = order.get("supplierItems")
                .find(el => (el.supplierItem.id || el.supplierItem.objectId) === targetOsiId)?.supplierItem
            const supplierItemId = fetchedOsi?.get("supplierItemId") || currentOrderSupplierItem?.supplierItemId || orderSupplierItem.supplierItem?.supplierItemId
            const units = fetchedOsi?.get("units") || orderSupplierItem.supplierItem?.units || orderSupplierItem.units

            // A lot detected on screen is kept only if it holds the same supplier item, otherwise a new lot is created
            for (const lot of [...lotsToUpdate]) {
                const existingLot = await getLot({ id: lot.existingLot.id || lot.existingLot.objectId, include: ["orderSupplierItem"], toJson: false })
                if (!isLotOfSupplierItem(existingLot, supplierItemId, units)) {
                    lotsToUpdate.splice(lotsToUpdate.indexOf(lot), 1)
                    lotsToCreate.push({ ...lot, existingLot: null })
                }
            }

            // Creation of new lots (sequential to avoid race conditions creating duplicates)
            for (const lot of lotsToCreate) {
                // Server-side re-check: another concurrent request may have already created this lot
                const alreadyExists = await getSameLotExists({
                    supplierItemId,
                    dlc: lot.dlc,
                    lotNumber: lot.lotNumber,
                    stockZone: lot.stockZone?.objectId,
                    units
                })

                if (alreadyExists) {
                    await alreadyExists.fetch()
                    // Merge into the existing lot instead of creating a duplicate
                    const events = alreadyExists.get("events") || []
                    const alreadyHasRecentReceptionForOsi = events.some(
                        e => e.mode === "RECEPTION" &&
                            e.orderSupplierItem?.objectId === currentOsiPointer.objectId &&
                            e.quantity === lot.quantity &&
                            (Date.now() - e.date) < 1500
                    )
                    if (!alreadyHasRecentReceptionForOsi) {
                        const receptionCount = events.filter(e => e.mode === "RECEPTION").length
                        events.push({
                            mode: "RECEPTION",
                            user: getLotEventUser(),
                            date: moment.utc().valueOf(),
                            quantity: lot.quantity,
                            receptionNumber: receptionCount + 1,
                            orderSupplierItem: currentOsiPointer
                        })
                        alreadyExists.set("events", events)
                        alreadyExists.set("quantity", (alreadyExists.get("quantity") || 0) + lot.quantity)
                        alreadyExists.set("receptionDate", moment.utc().startOf("day").valueOf())
                        await alreadyExists.save()
                    }

                    const mergedHasCreditNotes = await applyLotCreditNotes(alreadyExists, lot.id, creditNotes)
                    if (mergedHasCreditNotes) await alreadyExists.save()

                    createdLots.push(alreadyExists) // link back so undo can find it
                    continue
                }

                const newLot = await createLot({ ...lot, orderSupplierItem: { ...lot.orderSupplierItem, supplierItemId } }, currentOrderSupplierItem.supplierItem)
                const parseLot = await newLot.save()

                const hasCreditNotes = await applyLotCreditNotes(parseLot, lot.id, creditNotes)
                if (hasCreditNotes) await parseLot.save()

                createdLots.push(parseLot)
            }

            const supplierItemCreditNotesData = creditNotes.get("global") || []
            await Promise.all(supplierItemCreditNotesData.map(async creditNote => {
                const newCreditNote = await createCreditNote(creditNote.creditNote, creditNotesType.BROKEN, currentOrderSupplierItem.supplierItem)
                return newCreditNote
            }))

            // Mark the current supplier item as DONE
            currentOrderSupplierItem.receptionStatus = "DONE"

            if (lotsToUpdate.length > 0) {
                const updatedLots = []
                for (const lotObj of lotsToUpdate) {
                    const parseLot = lotObj.existingLot
                    await parseLot.fetch()
                    const events = parseLot.get("events") || []
                    const alreadyHasRecentReceptionForOsi = events.some(
                        e => e.mode === "RECEPTION" &&
                            e.orderSupplierItem?.objectId === currentOsiPointer.objectId &&
                            e.quantity === lotObj.quantity &&
                            (Date.now() - e.date) < 1500
                    )
                    let needsSave = false
                    if (!alreadyHasRecentReceptionForOsi) {
                        const receptionCount = events.filter(e => e.mode === "RECEPTION").length
                        events.push({
                            mode: "RECEPTION",
                            user: getLotEventUser(),
                            date: moment.utc().valueOf(),
                            quantity: lotObj.quantity,
                            receptionNumber: receptionCount + 1,
                            orderSupplierItem: currentOsiPointer
                        })
                        parseLot.set("events", events)
                        parseLot.set("quantity", (parseLot.get("quantity") || 0) + lotObj.quantity)
                        parseLot.set("receptionDate", moment.utc().startOf("day").valueOf())
                        needsSave = true
                    }

                    const hasCreditNotes = await applyLotCreditNotes(parseLot, lotObj.id, creditNotes)
                    needsSave = needsSave || hasCreditNotes

                    if (needsSave) {
                        const savedLot = await parseLot.save()
                        updatedLots.push(savedLot)
                    } else {
                        updatedLots.push(parseLot)
                    }
                }

                copyOrderSupplierItems.forEach(copyOrderSupplierItem => {
                    if (
                        (copyOrderSupplierItem.supplierItem.id || copyOrderSupplierItem.supplierItem.objectId)
                        === (orderSupplierItem.supplierItem.id || orderSupplierItem.supplierItem.objectId)
                    ) {
                        const prevLots = copyOrderSupplierItem.lots || []
                        // Only add updated lots that are not already referenced in prevLots (avoid duplicates)
                        const updatedLotIds = new Set(updatedLots.map(l => l.id || l.objectId))
                        const filteredPrevLots = prevLots.filter(l => !updatedLotIds.has(l.id || l.objectId))
                        copyOrderSupplierItem.lots = [...filteredPrevLots, ...updatedLots]
                    }
                })
            }

            // Link newly created lots and any previously orphaned lots back to the order supplier item
            const allLotsToLink = [...createdLots, ...alreadySavedLots]
            if (allLotsToLink.length > 0) {
                copyOrderSupplierItems.forEach(copyOrderSupplierItem => {
                    if (
                        (copyOrderSupplierItem.supplierItem.id || copyOrderSupplierItem.supplierItem.objectId)
                        === (orderSupplierItem.supplierItem.id || orderSupplierItem.supplierItem.objectId)
                    ) {
                        const prevLots = copyOrderSupplierItem.lots || []
                        // Avoid duplicating lot pointers that are already referenced in prevLots
                        const newLotIds = new Set(allLotsToLink.map(l => l.id || l.objectId))
                        const filteredPrev = prevLots.filter(l => !newLotIds.has(l.id || l.objectId))
                        copyOrderSupplierItem.lots = [...filteredPrev, ...allLotsToLink]
                    }
                })
            }

            // Restore each supplierItem to a raw Parse pointer before saving the Order.
            // cloneDeep of an included ParseObject produces a plain JS object whose own
            // properties (id, className, _objCount…) encode to {__type:"Object",…} —
            // Parse Server may treat that as a nested-object save and create a new empty
            // OSI instead of storing a pointer reference.  Using the raw pointer JSON
            // mirrors what Parse Server stores at Order-creation time, so the
            // getOrderByOrderSupplierItemId query (supplierItems.supplierItem.objectId)
            // continues to work after the re-save.
            copyOrderSupplierItems.forEach(item => {
                const osiId = item.supplierItem && (item.supplierItem.id || item.supplierItem.objectId)
                if (osiId) {
                    item.supplierItem = { __type: "Pointer", className: "OrderSupplierItem", objectId: osiId }
                }
            })

            order.set("supplierItems", copyOrderSupplierItems)

            return await order.save()
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function addLotCreditNote(lotId, creditNote, toJson = true) {
    try {
        const parseLot = await getLot({ id: lotId, toJson: false })

        let decrementedQuantity = 0
        const newCreditNote = await createCreditNote(creditNote, creditNotesType.LOT, parseLot)
        await addLotEvent(parseLot.id, {
            mode: "BROKEN",
            user: getLotEventUser(),
            date: moment.utc().valueOf(),
            quantity: newCreditNote.get("quantity"),
            reason: newCreditNote.get("reason"),
            comment: newCreditNote.get("comment"),
            images: newCreditNote.get("images")
        })
        decrementedQuantity += newCreditNote.get("quantity")


        parseLot.set("creditNotes", [newCreditNote])

        parseLot.set("quantity", parseLot.get("quantity") - decrementedQuantity)

        const savedLot = await parseLot.save()

        return toJson ? savedLot.toJSON() : savedLot
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function addOrderCreditNote(orderId, creditNotesData, toJson = false) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            let newCreditNotes = []
            const creditNotes = await Promise.all(creditNotesData.map(async item => await createCreditNote(item, creditNotesType.DELIVERY)))

            if (Array.isArray(order.get("creditNotes"))) {
                newCreditNotes = newCreditNotes.concat(order.get("creditNotes"), creditNotes)
            }
            else {
                newCreditNotes = newCreditNotes.concat(creditNotes)
            }

            order.set("creditNotes", newCreditNotes)

            await order.save()

            return toJson ? order.toJSON() : order
        }
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function updateOrderReceptionStatus(orderId, supplierItemId, toJson = true) {
    try {
        const order = await getOrder(orderId, false)

        if (order) {
            const supplierItems = [...order.get("supplierItems")]
            const currentSupplierItem = supplierItems.find(el => el.supplierItem.id === supplierItemId)

            if (currentSupplierItem) {
                if (!currentSupplierItem.quantities) currentSupplierItem.quantities = {}
                const newQuantities = { ...currentSupplierItem.quantities }

                if (newQuantities.reception) {
                    delete newQuantities.reception
                }

                if (newQuantities.deliveryNote) {
                    delete newQuantities.deliveryNote
                }
                currentSupplierItem.quantities = { ...newQuantities }
                currentSupplierItem.receptionStatus = "TODO"

                // Resolve lots to undo: prefer the linked lots array, but fall back to
                // querying by OrderSupplierItem pointer for orders where lots were never linked (old data)
                let lotsToUndo = currentSupplierItem.lots || []
                if (lotsToUndo.length === 0) {
                    const orderSupplierItemId = currentSupplierItem.supplierItem?.id || currentSupplierItem.supplierItem?.objectId
                    if (orderSupplierItemId) {
                        lotsToUndo = await getLotsByOrderSupplierItem([orderSupplierItemId], ["quantity", "events", "orderSupplierItem"], false)
                    }
                }

                for (const lot of lotsToUndo) {
                    const parseLot = await getLot({ id: lot.id || lot.objectId, toJson: false })
                    if (!parseLot) continue

                    const events = parseLot.get("events") || []
                    // RECEPTION events added by KFC-62 incrementation have a receptionNumber field
                    const incrementedEvents = events.filter(e => e.mode === "RECEPTION" && e.receptionNumber != null)

                    if (incrementedEvents.length > 0) {
                        // Pre-existing lot that was incremented — undo only the latest reception event
                        const latestEvent = incrementedEvents.reduce((latest, e) => e.date > latest.date ? e : latest, incrementedEvents[0])
                        const remainingEvents = events.filter(e => e !== latestEvent)
                        parseLot.set("events", remainingEvents)
                        parseLot.set("quantity", Math.max(0, (parseLot.get("quantity") || 0) - (latestEvent.quantity || 0)))
                        await parseLot.save()
                    } else {
                        // Freshly created lot — delete it entirely
                        await deleteLot(lot.id || lot.objectId)
                    }
                }

                currentSupplierItem.lots = []

                if (currentSupplierItem.creditNotes) {
                    for (const creditNote of currentSupplierItem.creditNotes) {
                        await deleteCreditNote(creditNote.id)
                    }

                    currentSupplierItem.creditNotes = []
                }

                order.set("supplierItems", supplierItems)
            }

            const updatedOrder = await order.save()

            return toJson ? updatedOrder.toJSON() : updatedOrder
        }

        return null
    }
    catch (e) {
        return Promise.reject(e)
    }
}

// Used to update isBillError field for order document
export async function updateIsBillingError(orderId, isBillingError) {
    try {
        const order = await new Parse.Query(Orders)
            .equalTo("objectId", orderId)
            .first()

        order.set("billError", isBillingError)

        return await order.save()
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function createOrderDuplicata(orderId) {
    try {
        await Parse.Cloud.run("duplicateOrder", { orderId })
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function getComingOrders(supplierItemId, toJson = true, siteId) {
    try {
        const orderSupplierItems = await getOrderSupplierItemsBySupplierItem(supplierItemId)
        const orderSupplierItemIds = orderSupplierItems.map(orderSupplierItem => orderSupplierItem.objectId)
        const sitePointer = new (Parse.Object.extend("Site"))({ id: siteId })

        const orders = await new Parse.Query(Orders)
            .equalTo("site", sitePointer)
            .greaterThanOrEqualTo("receptionDate", dayjs.tz().startOf("day").valueOf())
            .include(["supplierItems.receptionStatus"])
            .containedIn("receptionStatus", ["IN_PROGRESS", "TODO"])
            .equalTo("supplierItems.receptionStatus", "TODO")
            .containedIn("supplierItems.supplierItem.objectId", orderSupplierItemIds)
            .ascending("receptionDate")
            .limit(parseLimitRequest).find() || []

        return toJson ? orders.map((order) => order.toJSON()) : orders
    }
    catch (e) {
        return Promise.reject(e)
    }
}

export async function recalculateTotalAmountOrdersByDate(orderDate) {
    try {
        const orders = await new Parse.Query(Orders)
            .include(["supplierItems"])
            .greaterThanOrEqualTo("receptionDate", orderDate)
            .equalTo("totalAmount", 0)
            .equalTo("receptionStatus", "DONE")
            .limit(1000)
            .find()

        orders.forEach(async order => {
            const totalAmount = getOrderTotalAmount(order.toJSON())
            order.set("totalAmount", totalAmount)
            await order.save()
        })
    }
    catch (e) {
        console.error("parseManager.recipe.parseOrdersManager.recalculateTotalAmountOrders error : ", e)
        return Promise.reject(e)
    }
}